import { useEffect, useMemo, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import {
  IMonthlyCirculation,
  IMonthlyCirculationGridFilter,
} from "../api/types";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { GetMonthlyCirculation } from "../api/hooks";
import StyledDataGrid from "components/@extended/StyledDataGrid";
import api from "utils/api";
import { useForm } from "react-hook-form";
import DatePickerElement from "components/DatePickerElement";
import dayjs from "dayjs";
import { GetInstituteByLibrary } from "features/institute/api/hooks";
import { IDropdownModel } from "common/models/IDropdownModel";
import { SelectField } from "components/@extended/SelectField";
import LoadingButton from "components/@extended/LoadingButton";
import { AiFillFileExcel } from "react-icons/ai";
import { ExportButton } from "components/ExportButton";

const MonthlyCirculationGrid = () => {
  const [filter, setFilter] = useState<IMonthlyCirculationGridFilter>({
    year: dayjs().year(),
    instituteID: null,
  });

  const { data: getMonthlyCirculations, isFetching } =
    GetMonthlyCirculation(filter);
  const [exportLoading, setExportLoading] = useState(false);

  const { data: instituteQuery } = GetInstituteByLibrary();
  const [instituteOptions, setInstituteOptions] = useState<IDropdownModel[]>(
    [],
  );

  useEffect(() => {
    setInstituteOptions(instituteQuery?.apiData || []);
  }, [instituteQuery]);

  const methods = useForm<{ year: dayjs.Dayjs; instituteID: string | number | null }>({
    mode: "all",
    defaultValues: {
      year: dayjs().year(filter.year),
      instituteID: filter.instituteID,
    },
  });

  const { handleSubmit, control, reset } = methods;

  const onSubmit = handleSubmit((data) => {
    const year = dayjs(data.year).year();
    setFilter({ year, instituteID: data.instituteID });
  });

  const handleClear = () => {
    reset({ year: dayjs(), instituteID: null });
    setFilter({ year: dayjs().year(), instituteID: null });
  };

  const columns: GridColDef<IMonthlyCirculation>[] = useMemo(
    () => [
      {
        field: "libraryName",
        headerName: "Library Name",
        flex: 1.5,
      },
      {
        field: "instituteName",
        headerName: "Institute Name",
        flex: 2,
      },
      {
        field: "year",
        headerName: "Year",
        flex: 0.8,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "monthName",
        headerName: "Month",
        flex: 1,
        headerAlign: "center",
        align: "center",
      },
      {
        field: "totalIssue",
        headerName: "Total Issue",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <span style={{ color: "red" }}>{params.row.totalIssue}</span>
        ),
      },
      {
        field: "totalReturn",
        headerName: "Total Return",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <span style={{ color: "green" }}>{params.row.totalReturn}</span>
        ),
      },
      {
        field: "totalRenew",
        headerName: "Total Renew",
        flex: 1,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <span style={{ color: "blue" }}>{params.row.totalRenew}</span>
        ),
      },
    ],
    [],
  );

  const handleExport = async () => {
    setExportLoading(true);
    await api.downloadExcel(
      "api/Dashboard/exportMonthlyCirculation",
      "MonthlyCirculationStatistics",
      filter,
      setExportLoading,
    );
  };

  return (
    <>
      <Box component="form" onSubmit={onSubmit}>
        <Card elevation={3} sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h3" component={"h3"} marginBlockEnd={2}>
              Monthly Circulation Filter
              <Divider sx={{ my: 1 }} />
            </Typography>

            <Grid
              container
              spacing={2}
              alignItems="center"
              justifyContent="flex-start"
            >
              <Grid item xs={12} md={4} lg={3}>
                <DatePickerElement
                  control={control}
                  name="year"
                  label="Select Year"
                  views={["year"]}
                  format="YYYY"
                  sx={{ width: "100%" }}
                />
              </Grid>
              <Grid item xs={12} md={4} lg={3}>
                <SelectField
                  options={instituteOptions}
                  control={control}
                  name="instituteID"
                  label="Institute"
                  placeholder="Select Institute"
                />
              </Grid>
              <Grid item xs={12} md={4} lg={3}>
                <LoadingButton
                  variant="contained"
                  sx={{ mr: 1 }}
                  type="submit"
                  loading={isFetching}
                  disabled={isFetching}
                >
                  Search
                </LoadingButton>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleClear}
                  disabled={isFetching}
                >
                  Clear
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      <Card elevation={3} sx={{ mb: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h3" component={"h3"}>
              Monthly Circulation Statistics ({filter.year})
            </Typography>
            <ExportButton
              groupName="MonthlyCirculation"
              fileName={`MonthlyCirculation_${filter.year}`}
              reportTitle={`Monthly Circulation Statistics (${filter.year})`}
              params={() => filter}
              buttonText="Export"
            />
          </Box>
          <Divider sx={{ my: 1 }} />

          <Grid container sx={{ height: 500 }}>
            <StyledDataGrid
              sx={{
                "& .MuiDataGrid-columnHeaderTitle": {
                  whiteSpace: "wrap",
                  fontWeight: 600,
                  lineHeight: "normal",
                  fontSize: "13px",
                },
                "& .MuiDataGrid-cell": {
                  whiteSpace: "wrap",
                  alignContent: "center",
                  fontSize: 14,
                },
              }}
              density="compact"
              disableRowSelectionOnClick
              getRowId={(item) => item.uniqueID}
              columns={columns}
              rows={getMonthlyCirculations?.apiData || []}
              loading={isFetching}
              disableColumnFilter
              disableColumnSorting
              hideFooterPagination
            />
          </Grid>
        </CardContent>
      </Card>
    </>
  );
};

export default MonthlyCirculationGrid;
